import type jsPDF from "jspdf";
import { parseDescription, type DescriptionRun } from "./formatted-description";

/** Measures each font run before wrapping, so bold text never overlaps its neighbours. */
export function renderPdfDescription(options: {
  pdf: jsPDF; text: string; x: number; width: number; y: number;
  checkPageBreak: (y: number, height: number) => number;
}): number {
  const { pdf, text, x, width, checkPageBreak } = options;
  let y = options.y;
  function line(runs: DescriptionRun[], size = 9, heading = false, marker?: string) {
    const indent = marker ? 5 : 0;
    const available = width - indent;
    const height = size === 9 ? 4.4 : size * 0.3528 + 1.4;
    let segments: Array<{ text: string; style: string; width: number }> = [];
    let used = 0;
    let first = true;
    const flush = () => {
      y = checkPageBreak(y, height + 2);
      pdf.setFontSize(size);
      pdf.setTextColor(20, 20, 20);
      if (first && marker) {
        pdf.setFont("helvetica", "normal");
        pdf.text(marker, x, y);
      }
      let cursor = x + indent;
      for (const segment of segments) {
        pdf.setFont("helvetica", segment.style);
        pdf.text(segment.text, cursor, y);
        cursor += segment.width;
      }
      y += height;
      first = false;
      segments = []; used = 0;
    };
    const append = (value: string, style: string, measured: number) => {
      const last = segments[segments.length - 1];
      if (last?.style === style) { last.text += value; last.width += measured; }
      else segments.push({ text: value, style, width: measured });
      used += measured;
    };
    for (const run of runs) {
      const style = (run.bold || heading) ? (run.italic ? "bolditalic" : "bold") : (run.italic ? "italic" : "normal");
      for (const token of run.text.match(/\s+|\S+/g) ?? []) {
        pdf.setFont("helvetica", style); pdf.setFontSize(size);
        const measured = pdf.getTextWidth(token);
        if (used + measured > available && segments.length) flush();
        if (!used && /^\s+$/.test(token)) continue;
        if (measured <= available) append(token, style, measured);
        else {
          // Very long unbroken words must still fit inside the page margins.
          for (const char of Array.from(token)) {
            pdf.setFont("helvetica", style); pdf.setFontSize(size);
            const charWidth = pdf.getTextWidth(char);
            if (used + charWidth > available && segments.length) flush();
            append(char, style, charWidth);
          }
        }
      }
    }
    if (segments.length || first) flush();
  }
  for (const block of parseDescription(text)) {
    if (block.type === "blank") { y += 4.4; continue; }
    if (block.type === "list") {
      block.items.forEach((runs, index) => line(runs, 9, false, block.ordered ? `${block.start + index}.` : "•"));
    } else if (block.type === "heading") {
      y += 2;
      y = checkPageBreak(y, 14);
      line(block.runs, block.level === 1 ? 12 : block.level === 2 ? 11 : 10, true);
      y += 1;
    } else line(block.runs);
  }
  return y;
}
